import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowRight, Save, Eye, BrainCircuit, Layout, Type, Image as ImageIcon, 
  Video, FileAudio, Sliders, Trash2, RotateCw, X
} from 'lucide-react';
import { db } from '../firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';

const LessonPreviewOverlay = ({ elements, pageSettings, onClose }) => {
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/95 backdrop-blur-sm flex flex-col items-center overflow-y-auto custom-scrollbar direction-rtl">
      <div className="w-full max-w-[1000px] flex justify-between items-center p-4 sticky top-0 z-20">
        <div className="bg-white/10 text-white px-4 py-2 rounded-xl backdrop-blur-md border border-white/20 flex items-center gap-2">
          <Eye className="w-4 h-4 text-amber-400" />
          <span className="text-xs font-bold">وضع المستفيد (الطالب)</span>
        </div>
        <button onClick={onClose} className="flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white px-5 py-2 rounded-xl text-xs font-bold transition-all shadow-lg">
          إغلاق المعاينة <X className="w-4 h-4" />
        </button>
      </div>

      <div 
        className="relative shadow-[0_20px_50px_rgba(0,0,0,0.5)] mb-10 overflow-hidden rounded-md border border-white/10"
        style={{ width: pageSettings.width, minHeight: pageSettings.height, backgroundColor: pageSettings.backgroundColor }}
      >
        {elements.map((el) => (
          <div 
            key={el.id} className="absolute"
            style={{ left: el.x, top: el.y, width: el.width, height: el.height, opacity: el.opacity, borderRadius: el.cornerRadius, transform: `rotate(${el.rotation || 0}deg)`, overflow: 'hidden' }}
          >
            {el.type === 'text' && <div style={{ width: '100%', height: '100%', fontSize: `${el.fontSize}px`, fontFamily: el.fontFamily, color: el.color, textAlign: el.textAlign, fontWeight: el.fontWeight, padding: '8px' }}>{el.content}</div>}
            {el.type === 'image' && <img src={el.src} alt="وسائط" className="w-full h-full pointer-events-none" style={{ objectFit: 'fill' }} />}
            {el.type === 'video' && <video src={el.src} controls className="w-full h-full bg-black object-contain" />}
            {el.type === 'audio' && <div className="w-full h-full bg-slate-100 flex items-center justify-center p-2"><audio src={el.src} controls className="w-full" /></div>}
          </div>
        ))}
      </div>
    </div>
  );
};

const LessonBuilder = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // 1. الخوارزمية النظيفة: استرجاع البيانات الآمنة فقط
  const loadDraft = () => {
    try {
      const draft = localStorage.getItem('tamken_lesson_draft');
      if (draft) return JSON.parse(draft);
    } catch (error) {
      console.warn("تعذر قراءة الذاكرة المؤقتة.");
    }
    return null;
  };

  const draftData = loadDraft();

  const [lessonTitle, setLessonTitle] = useState(draftData?.lessonTitle || "نشاط / قصة بدون عنوان");
  const [coverImage, setCoverImage] = useState(draftData?.coverImage || null);
  const [pageSettings, setPageSettings] = useState(draftData?.pageSettings || { backgroundColor: '#f8fafc', width: 800, height: 1000 }); 
  const [elements, setElements] = useState(draftData?.elements || []);
  const [selectedId, setSelectedId] = useState(null);
  
  const [interactionState, setInteractionState] = useState({ type: null, targetId: null, startX: 0, startY: 0, startW: 0, startH: 0, startRot: 0, initialX: 0, initialY: 0 });
  const [guides, setGuides] = useState({ v: null, h: null }); 
  const [showPreview, setShowPreview] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const coverInputRef = useRef(null);
  
  const workspaceRef = useRef(null);
  const fileInputRef = useRef(null);
  const syncMediaTypeRef = useRef(null);

  // 2. حماية الانهيار (LocalStorage Payload Bomb Fix)
  useEffect(() => {
    try {
      // تفريغ الوسائط الثقيلة من العناصر قبل الحفظ المؤقت لتفادي QuotaExceededError
      const safeElements = elements.map(el => {
        if (el.isBase64 && (el.type === 'image' || el.type === 'video' || el.type === 'audio')) {
          // حفظ الإحداثيات فقط، وحذف ملف الوسائط الضخم لكي لا ينفجر المتصفح
          return { ...el, src: null, placeholder: true };
        }
        return el;
      });
      
      const safeCoverImage = coverImage && coverImage.length > 500000 ? null : coverImage;

      localStorage.setItem('tamken_lesson_draft', JSON.stringify({ 
        lessonTitle, 
        coverImage: safeCoverImage, 
        pageSettings, 
        elements: safeElements 
      }));
    } catch (error) { 
      console.warn("حجم البيانات كبير جداً على الذاكرة المؤقتة. تم تخطي الحفظ التلقائي لتجنب انهيار المتصفح.");
    }
  }, [lessonTitle, pageSettings, elements, coverImage]);

  useEffect(() => {
    if (elements.length === 0) return;
    const maxBottom = Math.max(...elements.map(el => el.y + (el.height === 'auto' ? 150 : el.height)));
    if (maxBottom > pageSettings.height - 200) {
      setPageSettings(prev => ({ ...prev, height: maxBottom + 400 }));
    }
  }, [elements, pageSettings.height]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Delete' || e.key === 'Backspace') {
        const activeTag = document.activeElement ? document.activeElement.tagName.toLowerCase() : '';
        if (activeTag !== 'input' && activeTag !== 'textarea' && activeTag !== 'select') {
          if (selectedId) { setElements(prev => prev.filter(el => el.id !== selectedId)); setSelectedId(null); }
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedId]);

  const handleWorkspaceClick = (e) => { if (e.target === workspaceRef.current) setSelectedId(null); };

  const handlePointerDown = (e, type, id) => {
    e.stopPropagation(); setSelectedId(id);
    const el = elements.find(el => el.id === id);
    if (!el) return;
    setInteractionState({ type, targetId: id, startX: e.clientX, startY: e.clientY, startW: el.width, startH: el.height !== 'auto' ? el.height : 100, startRot: el.rotation || 0, initialX: el.x, initialY: el.y });
  };

  const handlePointerMove = (e) => {
    if (!interactionState.type || !interactionState.targetId || !workspaceRef.current) return;
    const { type, targetId, startX, startY, startW, startH, initialX, initialY } = interactionState;
    const el = elements.find(el => el.id === targetId);
    if (!el) return;

    const deltaX = e.clientX - startX; const deltaY = e.clientY - startY;

    if (type === 'drag') {
      let newX = initialX + deltaX; let newY = initialY + deltaY;
      let newGuides = { v: null, h: null };
      const centerX = newX + el.width / 2; const centerY = newY + (el.height === 'auto' ? 50 : el.height) / 2;
      const SNAP_THRESHOLD = 8;

      if (Math.abs(centerX - pageSettings.width / 2) < SNAP_THRESHOLD) { newX = pageSettings.width / 2 - el.width / 2; newGuides.v = pageSettings.width / 2; }
      
      elements.forEach(other => {
        if (other.id === targetId) return;
        const otherCX = other.x + other.width / 2; const otherCY = other.y + (other.height === 'auto' ? 50 : other.height) / 2;
        if (Math.abs(newX - other.x) < SNAP_THRESHOLD) { newX = other.x; newGuides.v = other.x; } 
        else if (Math.abs(centerX - otherCX) < SNAP_THRESHOLD) { newX = otherCX - el.width / 2; newGuides.v = otherCX; } 
        if (Math.abs(newY - other.y) < SNAP_THRESHOLD) { newY = other.y; newGuides.h = other.y; } 
        else if (Math.abs(centerY - otherCY) < SNAP_THRESHOLD) { newY = otherCY - (el.height === 'auto' ? 50 : el.height) / 2; newGuides.h = otherCY; } 
      });

      setGuides(newGuides); handleUpdateElement(targetId, { x: newX, y: newY });
    } 
    else if (type.startsWith('resize')) {
      let newW = startW, newH = startH, newX = initialX, newY = initialY;
      const isCorner = type.includes('top-') || type.includes('bottom-');

      if (isCorner && el.type !== 'text') {
        const ratio = startW / startH;
        let delta = type.includes('right') ? deltaX : -deltaX;
        newW = startW + delta; newH = newW / ratio; 
        if (type.includes('left')) newX = initialX - (newW - startW);
        if (type.includes('top')) newY = initialY - (newH - startH);
      } else {
        if (type.includes('right')) { newW = startW + deltaX; }
        if (type.includes('left')) { newW = startW - deltaX; newX = initialX + deltaX; }
        if (type.includes('bottom')) { newH = startH + deltaY; }
        if (type.includes('top')) { newH = startH - deltaY; newY = initialY + deltaY; }
      }

      if (newW > 20) {
        let updates = { width: newW, x: newX };
        if (el.type !== 'text' && newH > 20) { updates.height = newH; updates.y = newY; } 
        else if (el.type === 'text') { updates.y = newY; }
        handleUpdateElement(targetId, updates);
      }
    }
    else if (type === 'rotate') {
      const workspaceRect = workspaceRef.current.getBoundingClientRect();
      const elCenterX = workspaceRect.left + initialX + startW / 2;
      const elCenterY = workspaceRect.top + initialY + startH / 2;
      const angleRad = Math.atan2(e.clientY - elCenterY, e.clientX - elCenterX);
      let angleDeg = (angleRad * 180) / Math.PI + 90; 
      const SNAP_ANGLE = 5;
      [0, 45, 90, 135, 180, -45, -90, -135, -180].forEach(snap => { if (Math.abs(angleDeg - snap) < SNAP_ANGLE) angleDeg = snap; });
      handleUpdateElement(targetId, { rotation: angleDeg });
    }
  };

  const handlePointerUp = () => { setInteractionState({ type: null, targetId: null }); setGuides({ v: null, h: null }); };
const handleFileUpload = async (e) => {
    const file = e.target.files[0]; 
    if (!file) return;
    
    const currentType = syncMediaTypeRef.current; 
    const newId = `el_${Date.now()}`;
    
    const currentScrollY = workspaceRef.current?.parentElement?.scrollTop || 0;
    const spawnY = currentScrollY + 150;

    // 1. إنشاء عنصر مؤقت يظهر للمستخدم فورا للإشارة إلى أن الرفع جارٍ
    let newElement = { 
      id: newId, type: currentType, x: 150, y: spawnY, 
      src: null, placeholder: true, // placeholder يعني جاري التحميل
      opacity: 0.7, cornerRadius: 8, rotation: 0 
    };
    
    if (currentType === 'image') { newElement.width = 300; newElement.height = 300; }
    if (currentType === 'video') { newElement.width = 400; newElement.height = 225; }
    if (currentType === 'audio') { newElement.width = 300; newElement.height = 50; }
    
    setElements(prev => [...prev, newElement]); 
    setSelectedId(newId);

    try {
      // 2. طلب الرابط الآمن من خادم Netlify الخاص بنا
      const response = await fetch('/.netlify/functions/upload', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fileName: file.name, fileType: file.type })
      });

      if (!response.ok) throw new Error('فشل في جلب تصريح الرفع');
      const { uploadUrl, fileUrl } = await response.json();

      // 3. الرفع المباشر إلى التخزين السحابي
         const uploadResponse = await fetch(uploadUrl, {
          method: 'PUT',
          headers: { 
          'Content-Type': file.type,
         'x-amz-acl': 'public-read' // <--- التدخل الجراحي يجب أن يكون داخل أقواس الـ headers
          },
         body: file
         });

      if (!uploadResponse.ok) throw new Error('فشل رفع الملف إلى السحابة');

      // 4. تحديث العنصر بالرابط النهائي وإزالة الـ Placeholder
      setElements(prev => prev.map(el => 
        el.id === newId ? { ...el, src: fileUrl, placeholder: false, isBase64: false, opacity: 1 } : el
      ));

    } catch (error) {
      console.error("Upload error:", error);
      alert("حدث خطأ أثناء رفع الملف. يرجى التحقق من اتصالك.");
      // إزالة العنصر المؤقت في حالة الفشل
      setElements(prev => prev.filter(el => el.id !== newId));
      setSelectedId(null);
    }
    
    e.target.value = null; // إعادة ضبط المدخل
  };

  const handleAddText = () => {
    const currentScrollY = workspaceRef.current?.parentElement?.scrollTop || 0;
    const spawnY = currentScrollY + 100;
    
    const newElement = { id: `el_${Date.now()}`, type: 'text', x: 100, y: spawnY, width: 300, height: 'auto', content: 'أدخل النص هنا...', fontSize: 32, fontFamily: 'Cairo', color: '#1e293b', textAlign: 'center', rotation: 0 };
    setElements([...elements, newElement]); setSelectedId(newElement.id);
  };

  const triggerFileUpload = (type) => {
    syncMediaTypeRef.current = type;
    if (type === 'image') fileInputRef.current.accept = 'image/*';
    if (type === 'video') fileInputRef.current.accept = 'video/*';
    if (type === 'audio') fileInputRef.current.accept = 'audio/*';
    fileInputRef.current.click();
  };

  const handleUpdateElement = (id, newProps) => setElements(elements.map(el => el.id === id ? { ...el, ...newProps } : el));

  const handleCoverUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      // طلب تصريح الرفع للغلاف
      const response = await fetch('/.netlify/functions/upload', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fileName: `cover-${Date.now()}-${file.name.replace(/\s+/g, '-')}`, fileType: file.type })
      });

      if (!response.ok) throw new Error('فشل تصريح الرفع');
      const { uploadUrl, fileUrl } = await response.json();

      //  الرفع المباشر إلى التخزين السحابي
         const uploadResponse = await fetch(uploadUrl, {
          method: 'PUT',
          headers: { 
          'Content-Type': file.type,
         'x-amz-acl': 'public-read' // <--- التدخل الجراحي يجب أن يكون داخل أقواس الـ headers
          },
         body: file
         });

      if (!uploadResponse.ok) throw new Error('فشل الرفع للسحابة');

      // زرع الرابط النظيف (بضع بايتات فقط) بدلاً من الصورة الكاملة
      setCoverImage(fileUrl);
    } catch (error) {
      console.error("Cover Upload Error:", error);
      alert("حدث خطأ أثناء رفع الغلاف. المرجو المحاولة مجدداً.");
    }
    e.target.value = null;
  };

  const handleSaveLesson = async () => {
    if (elements.length === 0) { 
      alert("الرجاء إضافة عناصر للدرس أولاً.");
      return; 
    }
    
    setIsSaving(true); setSelectedId(null);
    try {
      const autoCoverImage = elements.find(el => el.type === 'image' && el.src)?.src || "";
      const finalCover = coverImage || autoCoverImage;

      const sanitizedElements = elements.map(el => {
        const cleanEl = {};
        Object.keys(el).forEach(key => { if (el[key] !== undefined) cleanEl[key] = el[key]; });
        return cleanEl;
      });

      const safeCreatorId = user?.id || user?.uid; 

      if (!safeCreatorId) {
         throw new Error("لا يمكن حفظ النشاط لعدم التعرف على الهوية الأمنية للمؤطر.");
      }

      const activityData = {
        title: lessonTitle || "نشاط تعليمي جديد",
        coverImage: finalCover, 
        elements: sanitizedElements,
        pageSettings: pageSettings || { backgroundColor: '#f8fafc', width: 800, height: 1000 },
        type: 'lesson',
        author: user?.name || "مؤطر تمكين",
        creatorId: safeCreatorId, 
        createdAt: serverTimestamp(),
      };

      // القياس الفيزيائي الدقيق للنشاط بالكامل (شاملاً الغلاف)
      const totalDataSize = JSON.stringify(activityData).length;
      if (totalDataSize > 1000000) { 
        alert("🚨 عذراً، حجم النشاط كبير جداً وقاعدة البيانات سترفضه (يجب أن يكون أقل من 1 ميغابايت). السبب هو صورة الغلاف العملاقة. يرجى إزالة الغلاف أو رفع صورة أقل جودة.");
        setIsSaving(false);
        return;
      }

      await addDoc(collection(db, "library"), activityData);
      localStorage.removeItem('tamken_lesson_draft');
      
      // التوجيه الذكي إلى قسم أنشطتي بعد ظهور رسالة النجاح
      alert("تم حفظ النشاط بنجاح! سيتم توجيهك الآن إلى قسم أنشطتك لتبدأ العرض.");
      navigate('/workshop', { state: { activeTab: 'my_activities' } }); 

    } catch (error) {
      console.error(error); 
      alert(`حدث خطأ أثناء الحفظ: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };
  
  const selectedElement = elements.find(el => el.id === selectedId);
  const isInteracting = interactionState.type !== null;

  return (
    <div className="h-screen w-screen flex flex-col bg-[#0f172a] text-slate-300 font-sans overflow-hidden direction-rtl" onPointerMove={handlePointerMove} onPointerUp={handlePointerUp} onMouseLeave={handlePointerUp}>
      <input type="file" ref={fileInputRef} className="hidden" onChange={handleFileUpload} />

      <header className="h-16 bg-[#1e293b] border-b border-slate-800 flex items-center justify-between px-6 shrink-0 z-20 shadow-lg">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="p-2 hover:bg-slate-700 rounded-full transition-all text-slate-400 hover:text-white"><ArrowRight className="w-5 h-5" /></button>
          <div className="w-px h-6 bg-slate-700 mx-1"></div>
          <div className="flex items-center gap-3">
            <label className="w-10 h-10 rounded-xl bg-slate-800 border border-slate-700 flex items-center justify-center cursor-pointer hover:bg-slate-700 overflow-hidden transition-all shadow-inner shrink-0" title="إضافة صورة غلاف">
              <input type="file" className="hidden" accept="image/*" onChange={handleCoverUpload} />
              {coverImage ? <img src={coverImage} alt="غلاف" className="w-full h-full object-cover" /> : <ImageIcon className="w-4 h-4 text-slate-400" />}
            </label>
            <div className="flex items-center gap-4">
              <div className="flex flex-col">
                <input value={lessonTitle} onChange={(e) => setLessonTitle(e.target.value)} className="bg-transparent text-sm font-bold text-white outline-none focus:ring-2 focus:ring-blue-500 rounded px-2 py-1 w-64 transition-all" placeholder="أدخل اسم النشاط..." />
                <span className="text-[9px] text-slate-500 px-2 mt-0.5 uppercase tracking-widest">المرحلة 1: بناء السرد الحر</span>
              </div>
              
              <div className="flex items-center gap-2 border-r border-slate-700 pr-4 mr-4">
                <input type="file" ref={coverInputRef} className="hidden" accept="image/*" onChange={handleCoverUpload} />
                <button onClick={() => coverInputRef.current.click()} className="flex items-center gap-2 px-3 py-1.5 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-lg text-xs font-bold text-slate-300 transition-colors shadow-sm">
                  <ImageIcon className="w-4 h-4 text-blue-400" />
                  {coverImage ? "تغيير الغلاف" : "أضف صورة غلاف"}
                </button>
              </div>
            </div>          
          </div>        
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => setShowPreview(true)} className="px-4 py-2 text-xs font-bold text-amber-400 hover:text-amber-300 hover:bg-slate-800 flex items-center gap-2 transition-colors border border-slate-700 rounded-lg"><Eye className="w-4 h-4" /> معاينة الدرس</button>
          <button onClick={() => navigate('/studio', { state: { draft: { lessonTitle, pageSettings, elements, coverImage } } })} className="px-5 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white text-xs font-bold rounded-xl shadow-lg transition-all flex items-center gap-2 transform hover:scale-105 active:scale-95"><BrainCircuit className="w-4 h-4" /> إضافة لعبة (استوديو التفاعل)</button>
          <button onClick={handleSaveLesson} disabled={isSaving} className={`px-5 py-2 ${isSaving ? 'bg-blue-800' : 'bg-blue-600 hover:bg-blue-500'} text-white text-xs font-bold rounded-xl shadow-lg transition-all flex items-center gap-2`}><Save className="w-4 h-4" /> حفظ ونشر النشاط</button>        
        </div>
      </header>

      <div className="flex-1 flex overflow-hidden">
        <aside className="w-24 bg-[#1e293b] border-l border-slate-800 flex flex-col items-center py-6 gap-4 shrink-0 shadow-2xl z-10">
          <div className="p-2 bg-blue-500/10 rounded-xl mb-4 border border-blue-500/20"><Layout className="w-5 h-5 text-blue-400" /></div>
          <ToolIcon icon={<Type/>} label="نص" onClick={handleAddText} />
          <ToolIcon icon={<ImageIcon/>} label="صورة" onClick={() => triggerFileUpload('image')} />
          <ToolIcon icon={<Video/>} label="فيديو" onClick={() => triggerFileUpload('video')} />
          <ToolIcon icon={<FileAudio/>} label="صوت" onClick={() => triggerFileUpload('audio')} />
        </aside>

        <main className="flex-1 overflow-auto bg-slate-900 p-8 flex justify-center custom-scrollbar relative">
          <div ref={workspaceRef} onPointerDown={handleWorkspaceClick} className="shadow-2xl relative transition-colors duration-300" style={{ width: pageSettings.width, minHeight: pageSettings.height, backgroundColor: pageSettings.backgroundColor, backgroundImage: 'linear-gradient(rgba(0,0,0,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.05) 1px, transparent 1px)', backgroundSize: '20px 20px' }}>
            {guides.v !== null && <div className="absolute top-0 bottom-0 border-l border-red-500/50 z-0 pointer-events-none" style={{ left: guides.v }}></div>}
            {guides.h !== null && <div className="absolute left-0 right-0 border-t border-red-500/50 z-0 pointer-events-none" style={{ top: guides.h }}></div>}
            {elements.map((el) => (
              <div key={el.id} onPointerDown={(e) => handlePointerDown(e, 'drag', el.id)} className={`absolute group cursor-move ${selectedId === el.id ? 'z-50' : 'hover:ring-2 hover:ring-slate-300/50 z-10'}`} style={{ left: el.x, top: el.y, width: el.width, height: el.height, opacity: el.opacity, borderRadius: el.cornerRadius, transform: `rotate(${el.rotation || 0}deg)`, boxShadow: selectedId === el.id ? '0 0 0 2px #3b82f6' : 'none' }}>
                {selectedId === el.id && (
                  <>
                    <div className="absolute -top-10 left-1/2 -translate-x-1/2 flex flex-col items-center z-20"><div onPointerDown={(e) => handlePointerDown(e, 'rotate', el.id)} className="w-6 h-6 bg-white border border-slate-300 shadow-lg rounded-full flex items-center justify-center cursor-alias text-blue-500"><RotateCw className="w-3 h-3" /></div><div className="w-px h-4 bg-blue-500"></div></div>
                    <div onPointerDown={(e) => handlePointerDown(e, 'resize-top-left', el.id)} className="absolute -top-1.5 -left-1.5 w-3 h-3 bg-white border-2 border-blue-500 rounded-full cursor-nwse-resize z-20"></div>
                    <div onPointerDown={(e) => handlePointerDown(e, 'resize-top-right', el.id)} className="absolute -top-1.5 -right-1.5 w-3 h-3 bg-white border-2 border-blue-500 rounded-full cursor-nesw-resize z-20"></div>
                    <div onPointerDown={(e) => handlePointerDown(e, 'resize-bottom-left', el.id)} className="absolute -bottom-1.5 -left-1.5 w-3 h-3 bg-white border-2 border-blue-500 rounded-full cursor-nesw-resize z-20"></div>
                    <div onPointerDown={(e) => handlePointerDown(e, 'resize-bottom-right', el.id)} className="absolute -bottom-1.5 -right-1.5 w-3 h-3 bg-white border-2 border-blue-500 rounded-full cursor-nwse-resize z-20"></div>
                    <div onPointerDown={(e) => handlePointerDown(e, 'resize-top', el.id)} className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-white border-2 border-blue-500 rounded-full cursor-ns-resize z-20"></div>
                    <div onPointerDown={(e) => handlePointerDown(e, 'resize-bottom', el.id)} className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-white border-2 border-blue-500 rounded-full cursor-ns-resize z-20"></div>
                    <div onPointerDown={(e) => handlePointerDown(e, 'resize-left', el.id)} className="absolute top-1/2 -translate-y-1/2 -left-1.5 w-3 h-3 bg-white border-2 border-blue-500 rounded-full cursor-ew-resize z-20"></div>
                    <div onPointerDown={(e) => handlePointerDown(e, 'resize-right', el.id)} className="absolute top-1/2 -translate-y-1/2 -right-1.5 w-3 h-3 bg-white border-2 border-blue-500 rounded-full cursor-ew-resize z-20"></div>
                  </>
                )}
                     <div className="w-full h-full overflow-hidden flex items-center justify-center bg-transparent" style={{ borderRadius: el.cornerRadius }}>
                       {el.type === 'text' && <div style={{ width: '100%', height: '100%', fontSize: `${el.fontSize}px`, fontFamily: el.fontFamily, color: el.color, textAlign: el.textAlign, fontWeight: el.fontWeight, padding: '8px', cursor: selectedId === el.id ? 'text' : 'move' }}>{el.content}</div>}
                       {el.type === 'image' && <img src={el.src} alt="وسائط" className="w-full h-full pointer-events-none" style={{ objectFit: 'fill' }} />}
                      {el.type === 'video' && <video src={el.src} controls className={`w-full h-full bg-black object-contain ${isInteracting ? 'pointer-events-none' : ''}`} />}
                       {el.type === 'audio' && (
                         <div className="w-full h-full bg-slate-800 flex flex-col items-center justify-center rounded-xl border border-slate-600 shadow-inner pointer-events-none">
                           <FileAudio size={40} className="text-blue-400 mb-2" />
                           <span className="text-[10px] font-black text-slate-300">مسار صوتي</span>
                          </div>
                            )}
                      </div>
            </div>
            ))}
          </div>
        </main>

        <aside className="w-80 bg-[#1e293b] border-r border-slate-800 flex flex-col shrink-0 shadow-[-5px_0_15px_rgba(0,0,0,0.1)] z-10">
          <div className="p-4 border-b border-slate-800 flex items-center gap-2 text-xs font-bold text-slate-300"><Sliders className="w-4 h-4 text-blue-400" /> التخصيص والمظهر</div>
          <div className="flex-1 overflow-y-auto p-5">
            {!selectedElement ? (
              <div className="space-y-6">
                <div className="p-3 bg-slate-800/50 rounded-xl border border-slate-700/50 mb-4"><p className="text-xs text-slate-400 text-center leading-relaxed">أنت الآن تتحكم في <b>إعدادات الصفحة العامة</b>.</p></div>
                <div className="space-y-3"><h4 className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">لون الخلفية</h4><input type="color" value={pageSettings.backgroundColor} onChange={(e) => setPageSettings({...pageSettings, backgroundColor: e.target.value})} className="w-full h-10 rounded-md cursor-pointer bg-transparent border-0" /></div>
                <div className="space-y-3"><h4 className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">عرض الدرس</h4><input type="number" value={pageSettings.width} onChange={(e) => setPageSettings({...pageSettings, width: parseInt(e.target.value)})} className="w-full bg-[#0f172a] border border-slate-700 rounded-md text-xs text-white px-3 py-2 outline-none focus:border-blue-500" /></div>
                <div className="space-y-3"><h4 className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">طول الدرس</h4><input type="number" value={pageSettings.height} onChange={(e) => setPageSettings({...pageSettings, height: parseInt(e.target.value)})} className="w-full bg-[#0f172a] border border-slate-700 rounded-md text-xs text-white px-3 py-2 outline-none focus:border-blue-500" /></div>
              </div>
            ) : (
              <div className="space-y-6 animate-in fade-in duration-300">
                <div className="flex justify-between items-center mb-2"><span className="text-xs font-bold text-blue-400">تحرير عنصر</span><button onClick={() => { setElements(elements.filter(el => el.id !== selectedId)); setSelectedId(null); }} className="p-1.5 hover:bg-red-500/20 text-slate-500 hover:text-red-400 rounded transition-colors"><Trash2 className="w-4 h-4"/></button></div>
                <div className="p-3 bg-[#0f172a] rounded-xl border border-slate-800 space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1"><span className="text-[9px] text-slate-500">العرض</span><input type="number" value={Math.round(selectedElement.width)} onChange={(e) => handleUpdateElement(selectedId, { width: parseInt(e.target.value) })} className="w-full bg-[#1e293b] border border-slate-700 rounded text-xs text-white px-2 py-1 outline-none focus:border-blue-500" /></div>
                    <div className="space-y-1"><span className="text-[9px] text-slate-500">الدوران</span><input type="number" value={Math.round(selectedElement.rotation || 0)} onChange={(e) => handleUpdateElement(selectedId, { rotation: parseInt(e.target.value) })} className="w-full bg-[#1e293b] border border-slate-700 rounded text-xs text-white px-2 py-1 outline-none focus:border-blue-500" /></div>
                  </div>
                </div>
                {selectedElement.type === 'text' && (
                  <div className="space-y-4">
                    <textarea value={selectedElement.content} onChange={(e) => handleUpdateElement(selectedId, { content: e.target.value })} className="w-full bg-[#0f172a] border border-slate-700 rounded-lg text-xs text-white px-3 py-2 outline-none focus:border-blue-500 min-h-[80px]" />
                    <div className="grid grid-cols-2 gap-3">
                      <input type="number" value={selectedElement.fontSize} onChange={(e) => handleUpdateElement(selectedId, { fontSize: parseInt(e.target.value) })} className="w-full bg-[#0f172a] border border-slate-700 rounded text-xs text-white px-2 py-1 outline-none focus:border-blue-500" />
                      <input type="color" value={selectedElement.color} onChange={(e) => handleUpdateElement(selectedId, { color: e.target.value })} className="w-full h-6 rounded cursor-pointer bg-transparent border-0" />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </aside>
      </div>
      {showPreview && <LessonPreviewOverlay elements={elements} pageSettings={pageSettings} onClose={() => setShowPreview(false)} />}
    </div>
  );
};

const ToolIcon = ({ icon, label, onClick }) => ( <button onClick={onClick} className="flex flex-col items-center gap-1 group w-full px-2"><div className="w-12 h-12 flex items-center justify-center bg-slate-800/80 rounded-xl text-slate-400 group-hover:bg-blue-600 group-hover:text-white transition-all shadow-lg border border-slate-700">{React.cloneElement(icon, { size: 20 })}</div><span className="text-[10px] font-bold text-slate-500 group-hover:text-slate-300">{label}</span></button> );

export default LessonBuilder;